import React, { useEffect, useMemo } from 'react';
import Head, { DefaultAlternateVersions } from '@components/atoms/head/head';
import { useAnalytics } from '@context/analytics/analytics.context';
import { ContentsLocalizedPath } from '@interfaces/models/contentsLocalizedPath';
import { getPageMeta, ICMSTracking, IContentTypeEntries } from '@constants/metas/default';
import { CMS_LANDING_PAGES, ContentStackComponentKeys, ContentStackPageNames } from '@enums/contentStack';
import { usePreferences } from '@context/preferences.context';
import { useTranslation } from 'next-i18next';
import CMSPageContainer from './components/cms-page-containter';

const PAGE_VIEW_ANALYTICS = {
  [ContentStackPageNames.HOME_PAGE]: {
    screen_subcategory: 'homepage',
    screen_name: '/homepage',
    env_template: 'index_home',
  },
  [ContentStackPageNames.GLOBAL_HOME_PAGE]: {
    screen_subcategory: 'homepage',
    screen_name: '/homepage',
    env_template: 'index_home',
  },
  [ContentStackPageNames.EDITORIAL_PAGE]: { env_template: 'edito' },
};

const extractTracking = (trackingStr: string): string => {
  // Set default value as 'Empty' instead of '' to avoid mobile side crash issue
  if (!trackingStr) {
    return 'Empty';
  }
  const openParen = trackingStr.indexOf('(');
  const closeParen = trackingStr.lastIndexOf(')');
  if (openParen !== -1 && closeParen > openParen) {
    const extracted = trackingStr.substring(openParen + 1, closeParen).trim();
    return extracted || trackingStr;
  }
  return trackingStr;
};

const isCMSLandingPage = (contentTypeId: ContentStackPageNames) => {
  return contentTypeId && CMS_LANDING_PAGES.includes(contentTypeId);
};

const getCMSScreenCategory = (contentTypeId: ContentStackPageNames, trackingNew?: string) => {
  if (isCMSLandingPage(contentTypeId)) {
    if (trackingNew) {
      return trackingNew;
    }
    // Fallback to the legacy pattern when tracking_new is not provided
    return `landing_${contentTypeId}`;
  }

  return contentTypeId;
};

interface CMSPageProps {
  contentTypeEntries: IContentTypeEntries;
  alternateVersions?: ContentsLocalizedPath[];
}

const CMSPage: React.FC<CMSPageProps> = ({ contentTypeEntries, alternateVersions }): React.JSX.Element => {
  const { updateDataLayer } = useAnalytics();
  const { language } = usePreferences();
  const { t: tA11y } = useTranslation('a11y');

  const pageMeta = useMemo(() => {
    let metadata;
    const pageType = contentTypeEntries?.contentTypeId;

    if (CMS_LANDING_PAGES.includes(pageType)) {
      let descriptionBlock;
      const descriptionBlockKey = ContentStackComponentKeys.DESCRIPTION;

      if (pageType === ContentStackPageNames.CUSTOM_PAGE) {
        descriptionBlock = contentTypeEntries?.[ContentStackComponentKeys.MODULAR_BLOCK]?.find((modularBlock) => {
          return modularBlock[descriptionBlockKey];
        })?.[descriptionBlockKey]?.[descriptionBlockKey]?.[0];
      } else {
        descriptionBlock = contentTypeEntries?.[descriptionBlockKey]?.[0];
      }

      metadata = {
        title: contentTypeEntries?.displayTitle,
        description: descriptionBlock?.multi_line,
      };
    }

    if (pageType === ContentStackPageNames.EDITORIAL_PAGE) {
      metadata = {
        title: contentTypeEntries?.displayTitle,
        description: contentTypeEntries?.blockSubtitle,
      };
    }

    return getPageMeta(contentTypeEntries?.seo || {}, language, pageType, metadata);
  }, [contentTypeEntries]);

  useEffect(() => {
    if (!contentTypeEntries) {
      return null;
    }
    const { contentTypeId: contentTypeID, tracking, tracking_new } = contentTypeEntries || {};

    const trackingStr = typeof tracking === 'object' ? (tracking as ICMSTracking)?.tracking : (tracking as string);
    updateDataLayer({
      ...(PAGE_VIEW_ANALYTICS?.[contentTypeEntries?.contentTypeId] || {}),
      env_template: isCMSLandingPage(contentTypeID) ? 'channels' : contentTypeID,
      screen_name: trackingStr ? extractTracking(trackingStr) : 'Empty',
      screen_category: getCMSScreenCategory(contentTypeID, tracking_new),
      screen_subcategory: '',
    });
  }, [contentTypeEntries, updateDataLayer]);

  return (
    <>
      <Head
        metadata={pageMeta}
        alternateVersions={alternateVersions || DefaultAlternateVersions}
      />
      {isCMSLandingPage(contentTypeEntries?.contentTypeId) && (
        <h1 className="visuallyhidden">{tA11y('A11Y.LOGO_HEADING')}</h1>
      )}
      <CMSPageContainer {...{ contentTypeEntries }} />
    </>
  );
};

export default CMSPage;
